const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/appointment.ts';
import { createSelector } from 'reselect';

import { patAppUserCanSeeAppointment } from 'App/utils/appointment';
import { AppointmentType, StructuredAppointment } from '@cistec/common_web/defs/kisim';
import moment from 'moment/moment';
import { KisApiAction, ReducerState } from '@cistec/common_web/defs/actionTypes';
import {
    actionLoadFutureAppointments,
    actionLoadPastAppointments,
    actionLoadStructuredAppointment,
    BOOK_APPOINTMENT,
    CANCEL_APPOINTMENT,
    LOAD_PAST_STRUCTURED_APPOINTMENTS,
    LOAD_STRUCTURED_APPOINTMENT,
    LOAD_STRUCTURED_APPOINTMENTS,
    RESCHEDULE_APPOINTMENT,
} from 'App/actions/appointment';
import { manipulateStructuredAppointment } from './utils/appointments';
import {
    defaultKisApiActionSuccess,
    getData,
    getDataAsArray,
    makeReducer2,
    removeStructure,
    useRemoteData2,
} from '@cistec/common_web/utils/redux4Requests';
import { selectFeatureFlags, useFeatureFlags } from './featureFlags';
import { sortObjectsByPath } from '@cistec/common_web/utils/data';
import _ from 'lodash';
import { PatAppStoreState } from './setup/rootReducer';
import { useAllowedAppointmentTypes } from 'App/store/allowedAppointmentTypes';
import { getPatNrFromSession } from 'App/utils/sessionStorage';
import { FormatMessageFn, KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import textMap from './translations';

const KEY_APPOINTMENT_MAP = 'map';
const KEY_FUTURE_APPOINTMENTS = 'futureAppointments';
const KEY_PAST_APPOINTMENTS = 'pastAppointments';
const KEY_BOOK = 'book';
const KEY_CANCEL = 'cancel';
const KEY_RESCHEDULE = 'reschedule';

const getKeysForStructuredAppointment = (action: KisApiAction) => {
    return [KEY_APPOINTMENT_MAP, action.meta.terminNr];
};

const loadStructuredAppointmentSuccess = (state: ReducerState, action: KisApiAction) => {
    const appointment = action.payload.List[1];

    /* WARNING: MANIPULATING RESPONSE */
    manipulateStructuredAppointment(appointment);
    if (patAppUserCanSeeAppointment(appointment, action.meta.allowedAppointmentTypes)) {
        state = defaultKisApiActionSuccess(state, appointment, getKeysForStructuredAppointment(action));
    }
    return state;
};

const processStructuredAppointments = (state: ReducerState, action: KisApiAction, listKey: string): ReducerState => {
    const appointments = (action.payload || []) as StructuredAppointment[];

    /* WARNING: MANIPULATING RESPONSE */
    appointments.forEach(manipulateStructuredAppointment);

    appointments
        .filter(appointment => patAppUserCanSeeAppointment(appointment, action.meta.allowedAppointmentTypes))
        .forEach(appointment => {
            state = defaultKisApiActionSuccess(state, appointment, [KEY_APPOINTMENT_MAP, appointment.TERMNR]);
        });

    return defaultKisApiActionSuccess(state, `The data is stored in "${KEY_APPOINTMENT_MAP}"`, [listKey]);
};

const loadStructuredAppointmentsSuccess = (state: ReducerState, action: KisApiAction) =>
    processStructuredAppointments(state, action, KEY_FUTURE_APPOINTMENTS);

const loadPastStructuredAppointmentsSuccess = (state: ReducerState, action: KisApiAction) =>
    processStructuredAppointments(state, action, KEY_PAST_APPOINTMENTS);

const getKeysForBookAppointment = (action: KisApiAction) => {
    return [KEY_BOOK, action.meta.id];
};

const getKeysForCancelAppointment = (action: KisApiAction) => {
    return [KEY_CANCEL, action.meta.appointment.TERMNR];
};

const handleCancelAppointmentSuccess = (state: ReducerState, action: KisApiAction) => {
    const appointment = action.meta.appointment;
    // Remove the existing appointment from the store
    state = removeStructure(state, [KEY_APPOINTMENT_MAP, appointment.TERMNR]);
    state = defaultKisApiActionSuccess(state, action.payload, getKeysForCancelAppointment(action));
    return state;
};

const getKeysForRescheduleAppointment = (action: KisApiAction) => {
    return [KEY_RESCHEDULE, action.meta.appointment.TERMNR];
};

export const appointmentReducer = makeReducer2([
    [LOAD_STRUCTURED_APPOINTMENT, getKeysForStructuredAppointment, undefined, loadStructuredAppointmentSuccess],
    [LOAD_STRUCTURED_APPOINTMENTS, [KEY_FUTURE_APPOINTMENTS], undefined, loadStructuredAppointmentsSuccess],
    [LOAD_PAST_STRUCTURED_APPOINTMENTS, [KEY_PAST_APPOINTMENTS], undefined, loadPastStructuredAppointmentsSuccess],
    [BOOK_APPOINTMENT, getKeysForBookAppointment], // The created appointment is loaded by the appointment saga
    [CANCEL_APPOINTMENT, getKeysForCancelAppointment, undefined, handleCancelAppointmentSuccess],
    [RESCHEDULE_APPOINTMENT, getKeysForRescheduleAppointment], // The related appointment is loaded by the appointment saga
]);

// SELECTORS ==========================

export const selectReducerState = (state: PatAppStoreState): ReducerState => state.appointment;

const BELEGUNG_TYPE = 'BELEGUNG';

const createEntryAppointment = (appointment: StructuredAppointment): StructuredAppointment => ({
    ...appointment,
    STOPDAT: appointment.STARTDAT,
    RESNAME: `EINTRITT: ${appointment.RESNAME}`,
    titleFunction: (formatMessage: FormatMessageFn) => {
        return formatMessage(textMap.entry, { resname: appointment.RESNAME });
    },
});

const createExitAppointment = (appointment: StructuredAppointment): StructuredAppointment => ({
    ...appointment,
    STARTDAT: appointment.STOPDAT,
    RESNAME: `AUSTRITT: ${appointment.RESNAME}`,
    titleFunction: (formatMessage: FormatMessageFn) => {
        return formatMessage(textMap.leave, { resname: appointment.RESNAME });
    },
});

const modifyBelegungAppointments = (appointments: StructuredAppointment[]): StructuredAppointment[] =>
    appointments.flatMap(appointment =>
        appointment.TYP === BELEGUNG_TYPE
            ? [createEntryAppointment(appointment), createExitAppointment(appointment)]
            : [appointment]
    );

export const selectAppointments = createSelector(
    [selectReducerState],
    (state: ReducerState): StructuredAppointment[] | undefined => {
        const appointments = getDataAsArray(state, [KEY_APPOINTMENT_MAP]) as StructuredAppointment[] | undefined;
        return appointments ? modifyBelegungAppointments(appointments) : undefined;
    }
);

export const selectPastAppointments = createSelector([selectAppointments], appointments => {
    const now = moment();
    return (
        appointments && appointments.filter(appointment => moment(appointment.STOPDAT, KISIM_TIME_FORMAT).isBefore(now))
    );
});

export const selectFutureAppointments = createSelector([selectAppointments], appointments => {
    const now = moment();
    return (
        appointments &&
        appointments.filter(appointment => moment(appointment.STOPDAT, KISIM_TIME_FORMAT).isSameOrAfter(now))
    );
});

const makeSelectAppointment = (terminNr: string) =>
    createSelector([selectReducerState], (state: ReducerState): StructuredAppointment | undefined => {
        return getData(state, [KEY_APPOINTMENT_MAP, terminNr]);
    });

export const selectUpcomingAppointment = createSelector([selectFutureAppointments], futureAppointments => {
    return futureAppointments && _.clone(futureAppointments).sort(sortObjectsByPath('STARTDAT'))[0];
});

export const selectECheckinAppointment = (allowedAppointmentTypesForEcheckin: AppointmentType[]) =>
    createSelector([selectFutureAppointments], appointments => {
        if (appointments) {
            const appointmentsAllowedForEcheckin = appointments.filter(appointment =>
                allowedAppointmentTypesForEcheckin.includes(appointment.TYP)
            );
            return appointmentsAllowedForEcheckin[0];
        }
    });

export const selectUpcomingSurgeryAppointment = createSelector([selectFutureAppointments], futureAppointments => {
    if (futureAppointments) {
        const sortedFutureAppointments = _.clone(futureAppointments).sort(sortObjectsByPath('STARTDAT'));
        return sortedFutureAppointments.find(appointment => appointment.TYP === 'OPERATION');
    }
    return undefined;
});

export const selectCanUserBookAppointment = createSelector(
    [selectUpcomingAppointment, selectFeatureFlags],
    (upcomingAppointment, featureFlags) => {
        const patnr = getPatNrFromSession();
        return featureFlags.dashboard.appointmentBooking && (!!patnr || !upcomingAppointment);
    }
);

// HOOKS ====================

export const useStructuredAppointment = (terminNr: string) => {
    const { data: allowedAppointmentTypes, isPendingOrLoading: allowedAppointmentTypesIsLoading } =
        useAllowedAppointmentTypes();
    return useRemoteData2('appointment', {
        shouldRequest: !allowedAppointmentTypesIsLoading,
        request: dispatch => dispatch(actionLoadStructuredAppointment(terminNr, allowedAppointmentTypes)),
        keys: [KEY_APPOINTMENT_MAP, terminNr],
        select: makeSelectAppointment(terminNr),
    });
};

export const useFutureAppointments = () => {
    const { data: allowedAppointmentTypes, isPendingOrLoading: allowedAppointmentTypesIsLoading } =
        useAllowedAppointmentTypes();
    return useRemoteData2('appointment', {
        shouldRequest: !allowedAppointmentTypesIsLoading,
        request: dispatch => dispatch(actionLoadFutureAppointments(allowedAppointmentTypes)),
        keys: [KEY_FUTURE_APPOINTMENTS],
        select: selectFutureAppointments,
    });
};

export const useUpcomingAppointment = () => {
    const { data: allowedAppointmentTypes, isPendingOrLoading: allowedAppointmentTypesIsLoading } =
        useAllowedAppointmentTypes();
    return useRemoteData2('appointment', {
        shouldRequest: !allowedAppointmentTypesIsLoading,
        request: dispatch => dispatch(actionLoadFutureAppointments(allowedAppointmentTypes)),
        // keys need to be different for IAM user, because the response is a single appointment
        keys: [KEY_FUTURE_APPOINTMENTS],
        select: selectUpcomingAppointment,
    });
};

export const useUpcomingSurgeryAppointment = () => {
    const { data: allowedAppointmentTypes, isPendingOrLoading: allowedAppointmentTypesIsLoading } =
        useAllowedAppointmentTypes();
    return useRemoteData2('appointment', {
        shouldRequest: !allowedAppointmentTypesIsLoading,
        request: dispatch => dispatch(actionLoadFutureAppointments(allowedAppointmentTypes)),
        // keys need to be different for IAM user, because the response is a single appointment
        keys: [KEY_FUTURE_APPOINTMENTS],
        select: selectUpcomingSurgeryAppointment,
    });
};

export const usePastAppointments = () => {
    const { data: allowedAppointmentTypes, isPendingOrLoading: allowedAppointmentTypesIsLoading } =
        useAllowedAppointmentTypes();
    const { data: featureFlags } = useFeatureFlags();
    const { showPastAppointments } = featureFlags.appointment;

    return useRemoteData2('appointment', {
        shouldRequest: showPastAppointments && !allowedAppointmentTypesIsLoading,
        request: dispatch => dispatch(actionLoadPastAppointments(allowedAppointmentTypes)),
        keys: [KEY_PAST_APPOINTMENTS],
        select: selectPastAppointments,
    });
};
